import { getDbSafe, getAuthSafe } from './firebaseConfig';
import { doc, getDoc, onSnapshot, runTransaction, increment, serverTimestamp } from 'firebase/firestore';
import { FeedHideService } from './FeedHideService';

/**
 * Polls attached to yaps.
 *
 * Shape (see the `poll` field on Yap):
 *   yaps/{yapId}.poll = { options[], counts{}, totalVotes, closesAt }
 *   yaps/{yapId}/pollVotes/{uid} = { optionIndex, votedAt }
 *
 * `counts` is a map keyed by option index rather than an array because
 * Firestore cannot increment an array element. A dotted path
 * ("poll.counts.2") increments atomically, so simultaneous votes cannot lose
 * each other the way a read-modify-write of an array would.
 *
 * Changing a vote decrements the old option and increments the new one in the
 * SAME transaction as the vote document, so the counts can never disagree with
 * the per-user records.
 *
 * Closing is a timestamp comparison at write time, not a scheduled job: a poll
 * with `closesAt` in the past simply stops accepting votes. The rules enforce
 * the same bound so a stale client cannot vote past it.
 */

export const MAX_POLL_OPTIONS = 5;
export const MIN_POLL_OPTIONS = 2;
export const MAX_POLL_OPTION_LENGTH = 60;
/** Longest a poll may run. */
export const MAX_POLL_DAYS = 3;
/** Anonymous yaps are hard-deleted 24h after posting, taking the poll and every
 *  vote with them, so a poll on one cannot outlive that. */
export const MAX_ANONYMOUS_POLL_DAYS = 1;

export interface PollDraft {
    options: string[];
    days: number;
}

/**
 * Build the stored poll object from composer input. Returns null when unusable.
 *
 * Carries NO tallies. The yap document is world readable and Firestore returns
 * whole documents, so any count stored here would be readable before voting.
 * Counts live in the pollResults subcollection instead.
 */
export function buildPoll(draft: PollDraft): NonNullable<import('../types').Yap['poll']> | null {
    const options = draft.options.map(o => o.trim()).filter(Boolean).slice(0, MAX_POLL_OPTIONS);
    if (options.length < MIN_POLL_OPTIONS) return null;

    const days = Math.min(Math.max(1, Math.round(draft.days || 1)), MAX_POLL_DAYS);

    return {
        options,
        closesAt: new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString(),
    };
}

export interface PollResults {
    counts: Record<string, number>;
    totalVotes: number;
}

/** True once the poll has stopped accepting votes. */
export function isPollClosed(poll?: { closesAt?: string } | null): boolean {
    if (!poll?.closesAt) return false;
    return Date.now() >= new Date(poll.closesAt).getTime();
}

/** Human-readable time remaining, or "Final results" once closed. */
export function pollTimeRemaining(poll?: { closesAt?: string } | null): string {
    if (!poll?.closesAt) return '';
    const ms = new Date(poll.closesAt).getTime() - Date.now();
    if (ms <= 0) return 'Final results';
    const hours = Math.floor(ms / 3600000);
    if (hours >= 24) {
        const d = Math.floor(hours / 24);
        return `${d} day${d === 1 ? '' : 's'} left`;
    }
    if (hours >= 1) return `${hours} hour${hours === 1 ? '' : 's'} left`;
    const mins = Math.max(1, Math.floor(ms / 60000));
    return `${mins} min left`;
}

export const PollService = {
    /** This viewer's current choice, or null if they have not voted. */
    getMyVote: async (yapId: string, collectionName = 'yaps'): Promise<number | null> => {
        try {
            const auth = await getAuthSafe();
            if (!auth.currentUser) return null;
            const db = await getDbSafe();
            const snap = await getDoc(doc(db, collectionName, yapId, 'pollVotes', auth.currentUser.uid));
            if (!snap.exists()) return null;
            const idx = snap.data()?.optionIndex;
            return typeof idx === 'number' ? idx : null;
        } catch (e) {
            console.warn('[PollService] getMyVote failed:', e);
            return null;
        }
    },

    /**
     * The tally, or null when this viewer is not entitled to it.
     *
     * A null here is the security property working, not an error: the read rule
     * on pollResults requires that the caller has already voted or that the poll
     * has closed, so a permission-denied is the expected answer before voting.
     * Falls back to counts embedded on legacy polls created before tallies moved
     * out of the yap document.
     */
    fetchResults: async (
        yapId: string,
        collectionName = 'yaps',
        legacyPoll?: { counts?: Record<string, number>; totalVotes?: number } | null,
    ): Promise<PollResults | null> => {
        try {
            const db = await getDbSafe();
            const snap = await getDoc(doc(db, collectionName, yapId, 'pollResults', 'tally'));
            if (snap.exists()) {
                const d = snap.data() || {};
                return { counts: d.counts || {}, totalVotes: d.totalVotes || 0 };
            }
        } catch {
            // Denied (has not voted yet) or unreachable — fall through.
        }
        if (legacyPoll?.counts) {
            return { counts: legacyPoll.counts, totalVotes: legacyPoll.totalVotes || 0 };
        }
        return null;
    },

    /**
     * Turnout only: how many votes a poll has taken, with no indication of how
     * they split. Readable without having voted, which is the point — it lets a
     * poll show "42 votes" while the result stays hidden until you take part.
     *
     * Returns null when the document does not exist yet. A poll whose votes all
     * predate this document simply has no public total until its next vote, and
     * null renders as nothing rather than as a wrong "0 votes".
     */
    fetchPublicTotal: async (yapId: string, collectionName = 'yaps'): Promise<number | null> => {
        try {
            const db = await getDbSafe();
            const snap = await getDoc(doc(db, collectionName, yapId, 'pollResults', 'public'));
            const n = snap.exists() ? snap.data()?.totalVotes : null;
            return typeof n === 'number' ? n : null;
        } catch {
            return null;
        }
    },

    /**
     * Turnout, live.
     *
     * fetchPublicTotal reads it once, so a poll that gained votes while someone
     * was looking at it kept showing the number it had when the card mounted —
     * a poll on eight votes reading "5 votes" until the screen was left and
     * returned to.
     *
     * Only the TOTAL streams. The split stays in pollResults/tally, which is
     * unreadable until you have voted, so watching this reveals that a poll is
     * busy without revealing which way it is going.
     *
     * Returns an unsubscribe. Callers must invoke it: a listener left open on a
     * feed of poll cards is a listener per card, forever.
     */
    subscribeToPublicTotal: (
        yapId: string,
        collectionName: string,
        onChange: (total: number | null) => void,
    ): (() => void) => {
        let unsub: (() => void) | null = null;
        let cancelled = false;

        (async () => {
            try {
                const db = await getDbSafe();
                if (cancelled) return;
                unsub = onSnapshot(
                    doc(db, collectionName, yapId, 'pollResults', 'public'),
                    (snap) => {
                        const n = snap.exists() ? snap.data()?.totalVotes : null;
                        onChange(typeof n === 'number' ? n : null);
                    },
                    // A poll whose public document does not exist yet is not an
                    // error; it simply has no turnout to show.
                    () => onChange(null),
                );
            } catch {
                onChange(null);
            }
        })();

        return () => {
            cancelled = true;
            if (unsub) unsub();
        };
    },

    /**
     * Cast or change a vote. Resolves to the option now recorded, or null if the
     * write was rejected (poll closed, missing, or the option out of range).
     */
    castVote: async (
        yapId: string,
        optionIndex: number,
        collectionName = 'yaps',
    ): Promise<number | null> => {
        const auth = await getAuthSafe();
        if (!auth.currentUser) return null;
        const db = await getDbSafe();
        const userId = auth.currentUser.uid;

        const postRef = doc(db, collectionName, yapId);
        const voteRef = doc(db, collectionName, yapId, 'pollVotes', userId);
        const tallyRef = doc(db, collectionName, yapId, 'pollResults', 'tally');

        try {
            const applied = await runTransaction(db, async (tx) => {
                const postSnap = await tx.get(postRef);
                if (!postSnap.exists()) return null;

                const poll = postSnap.data()?.poll;
                if (!poll || !Array.isArray(poll.options)) return null;
                if (optionIndex < 0 || optionIndex >= poll.options.length) return null;
                // Read-time close check. The rules enforce this too; this keeps the
                // counts consistent even if a rule is ever relaxed.
                if (isPollClosed(poll)) return null;

                // A vote is final. Once one exists it stands, whichever option
                // was tapped — the rules deny the update too, so this is a fast
                // exit rather than the enforcement. Returning the existing choice
                // keeps the UI showing what is actually recorded.
                const voteSnap = await tx.get(voteRef);
                if (voteSnap.exists()) {
                    const existing = voteSnap.data()?.optionIndex;
                    return typeof existing === 'number' ? existing : null;
                }

                // The tally is deliberately NOT read here. A voter has no read
                // access to it until their vote exists, which is the whole point
                // of moving it out of the yap; reading it inside the transaction
                // would fail for exactly the people allowed to write it.
                // increment() needs no prior value, and set(merge) creates the
                // document on the first vote.
                // Always a first vote by this point, so the tally only ever goes
                // up — no decrement path, because a vote cannot move.
                tx.set(tallyRef, {
                    counts: { [String(optionIndex)]: increment(1) },
                    totalVotes: increment(1),
                }, { merge: true });
                // Turnout is NOT written here any more.
                //
                // It lives in its own document because it is readable before
                // voting while the split above is not, and Firestore grants reads
                // per document rather than per field. But a client-incremented
                // turnout is only as correct as the oldest bundle still running:
                // production had a poll reading 2 public against 9 in the tally,
                // which one atomic transaction cannot produce — the missing votes
                // came from clients on a build that predated this document
                // existing at all.
                //
                // The onPollVoteWritten trigger now derives it by counting the
                // pollVotes documents below, so it is right regardless of what
                // any client version writes. See functions/pollTotals.js.
                tx.set(voteRef, { userId, optionIndex, votedAt: serverTimestamp() });
                return optionIndex;
            });

            // Voting is engagement, so it holds the yap out of the World feed for
            // 48h like a like or a reply does. No post data is needed to normalize
            // the hide key: a poll only ever lives on the yap itself, never on a
            // shout, so the key is just the yap id.
            if (applied !== null) {
                void FeedHideService.recordHide(yapId, 'vote', collectionName);
            }
            return applied;
        } catch (e) {
            console.error('[PollService] castVote failed:', e);
            return null;
        }
    },
};
